
// Auto-pagination (syntax fixed by Luca, 2026-07-10; cloned for TVStor 2026-08-02):
// `next()` yields ONE item per call, null at exhaustion; `for await` is a trivial
// wrapper. Concurrent next() calls are serialized on an internal promise chain so
// they never double-fetch a page.

export type PaginatedFetch<T> = (params: { cursor: string | null }) => Promise<{ items: T[]; nextCursor: string | null; stale?: boolean }>;

export interface PageIterator<T> extends AsyncIterable<T> {
    next(): Promise<T | null>;
    // The page envelope's freshness marker (cluster-first §6): true once any
    // fetched page was served from the stale replica fallback — undefined before
    // the first fetch. Read it AFTER iterating to label the collected list.
    stale(): boolean | undefined;
}



export function genPageIterator<T>(fn: PaginatedFetch<T>): PageIterator<T> {
    let started = false;
    let ended = false;
    let sawStale: boolean | undefined;
    let cache: T[] = [];
    let lastCursor: string | null = null;
    let chain: Promise<T | null> = Promise.resolve(null);

    const advance = async (): Promise<T | null> => {
        if (ended) {
            return null;
        }
        if (cache.length === 0) {
            if (started && lastCursor === null) {
                ended = true;
                return null;
            }
            const out = await fn({ cursor: lastCursor });
            started = true;
            sawStale = (sawStale ?? false) || out.stale === true;
            cache = out.items;
            lastCursor = out.nextCursor;
            if (cache.length === 0) {
                // The cursor engine never emits nextCursor on an empty page, so an
                // empty fetch is always a legitimate end (offset past the tail after
                // concurrent deletions included).
                ended = true;
                return null;
            }
        }
        return cache.shift() ?? null;    // resources are objects, never null
    };

    const next = (): Promise<T | null> => {
        chain = chain.then(advance, advance);
        return chain;
    };

    return {
        next,
        stale: (): boolean | undefined => sawStale,
        async *[Symbol.asyncIterator](): AsyncGenerator<T> {
            for (let item = await next(); item !== null; item = await next()) {
                yield item;
            }
        },
    };
}

